/**
 * Findings queue state ⇄ URL. Everything that shapes the list lives in
 * the query string so a view is shareable: `?view=fix_next&sev=critical`,
 * a saved view `?view=<uuid>` (its query fills the rest), and the open
 * detail pane `&f=<canonicalId>`.
 */
import {
  type FindingQueueQuery,
  findingQueueQuerySchema,
  QUEUE_SYSTEM_VIEWS,
  type QueueSystemView,
} from "@iamu/shared";
import {
  periodQuery,
  setPeriodParams,
} from "@/components/findings-scope/scope-url-state";

export interface QueueUrlState {
  /** System view, or null when a saved view (`savedViewId`) is active. */
  view: QueueSystemView | null;
  savedViewId: string | null;
  query: FindingQueueQuery;
  /** Canonical open in the detail pane. */
  focusId: string | null;
}

const LIST_KEYS = {
  sev: "severities",
  state: "states",
} as const;
const SCALAR_KEYS = {
  type: "type",
  cap: "capabilityId",
  capScope: "capabilityScope",
  outline: "outlineId",
  persona: "personaId",
  lens: "lens",
  run: "firstSeenRunId",
  search: "search",
  area: "area",
  env: "environment",
  branch: "branch",
  sort: "sort",
  group: "group",
  density: "density",
  request: "request",
  /** A period summary bucket (`PERIOD_BUCKET_PARAM`). */
  pb: "periodBucket",
} as const;

const isSystemView = (v: string | null): v is QueueSystemView =>
  !!v && (QUEUE_SYSTEM_VIEWS as readonly string[]).includes(v);

export function parseQueueUrl(params: URLSearchParams): QueueUrlState {
  const rawViewParam = params.get("view");
  // "queue" is the tab name, not a system view or saved view. The old
  // Overdue view (SLA countdown, removed) lands on Ready to fix.
  const rawView =
    rawViewParam === "queue"
      ? null
      : rawViewParam === "overdue"
        ? "fix_next"
        : rawViewParam;
  const raw: Record<string, unknown> = {};
  for (const [param, key] of Object.entries(LIST_KEYS)) {
    const v = params.get(param);
    if (v) raw[key] = v.split(",").filter(Boolean);
  }
  for (const [param, key] of Object.entries(SCALAR_KEYS)) {
    const v = params.get(param);
    if (v) raw[key] = v;
  }
  if (params.get("judge") === "1") raw.judgeCited = true;
  if (params.get("committed") === "1") raw.committedOnly = true;
  Object.assign(raw, periodQuery(params));
  // The page-wide scope (period, persona, capability, severity, lens, build)
  // narrows whichever view is open; it never replaces the default view.
  const hasFilterParams = Boolean(
    (raw.states as string[] | undefined)?.length ||
      raw.area ||
      raw.outlineId ||
      raw.type ||
      raw.search ||
      raw.judgeCited ||
      raw.request ||
      raw.periodBucket,
  );
  const view = isSystemView(rawView)
    ? rawView
    : rawView
      ? null
      : hasFilterParams
        ? null
        : "fix_next";
  if (view) raw.view = view;
  // Drop anything invalid rather than failing the page.
  const parsed = findingQueueQuerySchema.safeParse(raw);
  const query = parsed.success
    ? parsed.data
    : ({ view: view ?? undefined } as FindingQueueQuery);
  return {
    view,
    savedViewId: view ? null : rawView && rawView !== "queue" ? rawView : null,
    query,
    focusId: params.get("f"),
  };
}

/** URL params for a query (the system view or saved view id included). */
export function queueUrlParams(
  state: Pick<QueueUrlState, "view" | "savedViewId" | "query" | "focusId">,
): URLSearchParams {
  const p = new URLSearchParams();
  const activeView = state.view ?? state.savedViewId;
  if (activeView) {
    p.set("view", activeView);
  }
  const q = state.query as Record<string, unknown>;
  for (const [param, key] of Object.entries(LIST_KEYS)) {
    const v = q[key] as string[] | undefined;
    if (v?.length) p.set(param, v.join(","));
  }
  for (const [param, key] of Object.entries(SCALAR_KEYS)) {
    const v = q[key];
    if (typeof v === "string" && v) p.set(param, v);
  }
  if (q.judgeCited) p.set("judge", "1");
  if (q.committedOnly) p.set("committed", "1");
  setPeriodParams(p, state.query.period, state.query.periodMode);
  if (state.focusId) p.set("f", state.focusId);
  return p;
}

/** The query sent to the server: the saved view's, overlaid by the URL's. */
export function effectiveQuery(
  state: QueueUrlState,
  savedQuery: FindingQueueQuery | null,
): FindingQueueQuery {
  if (state.view) return state.query;
  return { ...(savedQuery ?? {}), ...state.query };
}
